import { formatUserName } from './formatUserName';
import { userInitials } from './userInitials';
// src/components/shared/MenuLayout.jsx
import React, { useState, useEffect, useId } from 'react';
import { Box, Typography, Avatar, ButtonBase, Dialog, Menu, MenuItem, Divider } from '@mui/material';
import { Logout, Home, KeyboardArrowDownRounded, ArrowBackRounded, AppsRounded, AccountCircleOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ICONOS_MODULOS } from './ModuloIconos';
import { authService } from '../../services/api';
import api from '../../services/api';
import './MenuLayout.css';

// ── Paleta iOS ─────────────────────────────────────────────────────────────────
export const IOS = {
  bg: '#F3F5FA', surface: '#FFFFFF',
  separator: 'rgba(60,60,67,0.12)',
  label: '#1C1C1E', label2: '#687184', label3: '#858D9D',
  blue: '#007AFF', purple: '#5E5CE6', green: '#34C759', red: '#FF3B30',
  orange: '#FF9500', teal: '#5AC8FA', yellow: '#FFCC00', pink: '#FF2D55',
};

// ── Colores por módulo/opción ──────────────────────────────────────────────────
export const COLOR = {
  admision:'#007AFF', emergencia:'#FF3B30', caja:'#34C759', consultorios:'#007AFF',
  economia:'#FF9500', ecografia:'#5AC8FA', farmacia:'#5E5CE6',datafarma:'#007AFF', hospitalizacion:'#007AFF',
  laboratorio:'#FF9500', rayosx:'#5AC8FA', seguros:'#34C759', usuarios:'#5E5CE6',
  ventas:'#5E5CE6', anulaciones:'#FF3B30', kardex:'#5E5CE6', inventario:'#FF9500',
  ajustes:'#8E8E93', stock:'#34C759', almacen:'#007AFF', productos:'#5E5CE6',
  reporte:'#34C759', indicadores:'#FF9500', consumos:'#5AC8FA', consumopac:'#007AFF',
  requerimiento:'#5E5CE6', narcoticos:'#FF3B30', psicotropicos:'#FF9500',
  conteo:'#8E8E93', devoluciones:'#007AFF',
  comprobantes:'#FF9500', 'libro-ventas':'#5E5CE6', 'consulta-cpe':'#007AFF', 'monitor-envios':'#34C759',
  roles: '#5E5CE6', tablero: '#5E5CE6',
};

// ── SVG base ───────────────────────────────────────────────────────────────────
const I = ({ c, children }) => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.65"
    strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

// ── Aviso de uso (pagos pendientes) ─────────────────────────────────────────────
const fmtFechaLimite = (iso) => { if(!iso) return ''; const [y,m,d]=iso.split('-'); return `${d}/${m}/${y}`; };

const UsoPopup = ({ open, onClose, estado, fechaLimite, diasRestantes, diasParaBloqueoTotal }) => {
  if (!open) return null;
  const esCritico = estado==='bloqueoInminente' || estado==='bloqueoTotal';
  const headerBg = esCritico ? '#FEE2E2' : '#FFF8E6';
  const headerBorder = esCritico ? '#FCA5A5' : '#FFE9A0';
  const iconBg = esCritico ? '#FCA5A5' : '#FFE9A0';
  const iconColor = esCritico ? '#991B1B' : '#8B6914';

  const titulo = {
    porVencer: 'Pagos pendientes',
    reducido: 'Servicio en modo reducido',
    bloqueoInminente: 'Suspensión total próxima',
    bloqueoTotal: 'Servicio suspendido',
    prorroga: 'Pagos pendientes — prórroga otorgada',
  }[estado] || 'Aviso';

  const cuerpo = {
    porVencer: `Este establecimiento tiene pagos pendientes. El servicio completo estará disponible hasta el ${fmtFechaLimite(fechaLimite)}${diasRestantes!=null?` (${diasRestantes} día${diasRestantes===1?'':'s'})`:''}. Después de esa fecha, el sistema pasará a modo reducido, con acceso solo a consulta de stock.`,
    reducido: 'Este establecimiento tiene pagos pendientes y el sistema está funcionando en modo reducido — solo se puede consultar stock.',
    bloqueoInminente: `El sistema sigue en modo reducido, pero en ${diasParaBloqueoTotal} día${diasParaBloqueoTotal===1?'':'s'} se suspenderá por completo si no se regulariza el pago pendiente.`,
    bloqueoTotal: 'El servicio se encuentra completamente suspendido por pagos pendientes. Ningún módulo está disponible hasta que se regularice el pago.',
    prorroga: 'Este establecimiento tiene pagos pendientes, pero se otorgó una prórroga temporal — todas las funciones del sistema están disponibles con normalidad mientras se regulariza el pago.',
  }[estado] || '';

  return (
    <Dialog open={open} onClose={onClose} aria-label={titulo}
      PaperProps={{ sx: { borderRadius:'24px', m:2, maxWidth:400 } }}>
      <Box onClick={e=>e.stopPropagation()}
        sx={{ bgcolor:IOS.surface, borderRadius:'20px', overflow:'hidden', width:400, maxWidth:'100%',
          boxShadow:'0 24px 60px rgba(0,0,0,0.25)' }}>
        <Box sx={{ bgcolor:headerBg, px:3, py:2.5, borderBottom:`1px solid ${headerBorder}`,
          display:'flex', gap:1.5, alignItems:'flex-start' }}>
          <Box sx={{ width:34, height:34, borderRadius:'10px', bgcolor:iconBg, flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </Box>
          <Box>
            <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:iconColor, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>
              Aviso Importante
            </Typography>
            <Typography sx={{ fontSize:'0.95rem', fontWeight:700, color:'#1C1C1E' }}>{titulo}</Typography>
          </Box>
        </Box>
        <Box sx={{ px:3, py:2.5 }}>
          <Typography sx={{ fontSize:'0.85rem', color:'#3A3A3C', lineHeight:1.6, mb:2 }}>{cuerpo}</Typography>
          <Box sx={{ p:1.5, borderRadius:'12px', bgcolor:'#EDFAF2', border:'1px solid #B7EFD0', mb:2 }}>
            <Typography sx={{ fontSize:'0.76rem', color:'#1A6B3C', fontWeight:600 }}>
              {estado==='bloqueoTotal'
                ? 'Únicamente el envío electrónico de comprobantes a SUNAT continúa funcionando con total normalidad.'
                : 'La emisión electrónica a SUNAT y el envío de información de las IPRESS como stock, ICI, y ventas, continúan funcionando con total normalidad.'}
            </Typography>
          </Box>
          <Box sx={{ p:1.5, borderRadius:'12px', bgcolor:IOS.bg, border:`1px solid ${IOS.separator}`, mb:2,
            display:'flex', alignItems:'center', gap:1.2 }}>
            <Box sx={{ width:32, height:32, borderRadius:'9px', bgcolor:`${IOS.blue}12`, flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2.5"/>
                <path d="M2.5 6.5l9.5 7 9.5-7"/>
              </svg>
            </Box>
            <Box sx={{ minWidth:0 }}>
              <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, fontWeight:600, mb:0.1 }}>
                Consultas sobre tu cuenta
              </Typography>
              <Typography component="a" href="mailto:ventas@ardata.com.pe"
                sx={{ fontSize:'0.8rem', color:IOS.blue, fontWeight:700, textDecoration:'none',
                  '&:hover':{ textDecoration:'underline' } }}>
                ventas@ardata.com.pe
              </Typography>
            </Box>
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ width:'100%', height:38, borderRadius:'10px', border:'none', bgcolor:IOS.blue,
              cursor:'pointer', color:'white', fontSize:'0.85rem', fontWeight:700 }}>
            Entendido
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

const UsoBanner = ({ estado, fechaLimite, diasParaBloqueoTotal }) => {
  const esCritico = estado==='bloqueoInminente' || estado==='bloqueoTotal';
  const bg = esCritico ? '#FEE2E2' : '#FFF8E6';
  const border = esCritico ? '#FCA5A5' : '#FFE9A0';
  const txt = esCritico ? '#991B1B' : '#8B6914';

  const mensaje = () => {
    switch (estado) {
      case 'porVencer':
        return `Pagos pendientes — servicio completo hasta el ${fmtFechaLimite(fechaLimite)}. Luego pasará a modo reducido.`;
      case 'reducido':
        return 'Pagos pendientes — sistema en modo reducido (solo consulta de stock en Farmacia). SUNAT y DIGEMID no se ven afectados.';
      case 'bloqueoInminente':
        return `Pagos pendientes — en ${diasParaBloqueoTotal} día${diasParaBloqueoTotal===1?'':'s'} el sistema se suspenderá por completo. Regulariza el pago para evitar la interrupción total del servicio.`;
      case 'bloqueoTotal':
        return 'Servicio suspendido por pagos pendientes. Solo el envío de comprobantes a SUNAT continúa activo. Regulariza el pago para restablecer el acceso.';
      case 'prorroga':
        return 'Pagos pendientes — se otorgó una prórroga temporal, todas las funciones están disponibles con normalidad.';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ bgcolor:bg, borderBottom:`1px solid ${border}`, px:{xs:2,sm:4}, py:0.8,
      display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={txt} strokeWidth="2.2" strokeLinecap="round" style={{flexShrink:0}}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <Typography sx={{ fontSize:'0.72rem', color:txt, fontWeight:600, textAlign:'center' }}>{mensaje()}</Typography>
    </Box>
  );
};

// ── Header ─────────────────────────────────────────────────────────────────────
const AccountMenuSummary = ({children}) => <li className="integra-account-summary" role="presentation">{children}</li>;
AccountMenuSummary.muiSkipListHighlight = true;

export const HeaderIOS = ({ titulo, subtitulo, onHome, onLogout, nombreCompleto, avatarLetra }) => {
  const navigate = useNavigate();
  const menuId = useId();
  const [accountAnchor, setAccountAnchor] = useState(null);
  const accountOpen = Boolean(accountAnchor);
  const accountName = formatUserName(nombreCompleto) || 'Mi cuenta';
  const initials = accountName === 'Mi cuenta' ? (avatarLetra || 'U') : userInitials(authService.getCurrentUser(), accountName);
  const closeAccount = () => setAccountAnchor(null);
  const logout = () => { closeAccount(); if(onLogout) onLogout(); else { authService.logout(); navigate('/login'); } };
  const [uso, setUso] = useState(null);
  const [popupAbierto, setPopupAbierto] = useState(false);
  const [bannerOculto, setBannerOculto] = useState(false);

  useEffect(() => {
    const user = authService.getCurrentUser();
    const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento;
    if (!ueId) return;
    api.get('/sistema/uso/validar', { params:{ ueId } })
      .then(r => {
        if (r.data?.estado && r.data.estado !== 'normal') {
          setUso(r.data);
          const hoy = new Date().toISOString().split('T')[0];
          if (sessionStorage.getItem('usoOcultoHasta') === hoy) setBannerOculto(true);
          if (!sessionStorage.getItem('usoPopupVisto')) {
            setPopupAbierto(true);
            sessionStorage.setItem('usoPopupVisto', '1');
          }
        }
      })
      .catch(()=>{});
  }, []);

  const ocultarBannerHoy = () => {
    sessionStorage.setItem('usoOcultoHasta', new Date().toISOString().split('T')[0]);
    setBannerOculto(true);
  };

  return (
  <>
  <Box component="header" className="integra-header" sx={{ position:'sticky', top:0, zIndex:100,
    bgcolor:'rgba(248,250,255,0.82)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
    borderBottom:`1px solid ${IOS.separator}` }}>
    {uso && !bannerOculto && (
      <UsoBanner estado={uso.estado} fechaLimite={uso.fechaLimite} diasParaBloqueoTotal={uso.diasParaBloqueoTotal} />
    )}
    <div className="integra-topbar">
      <div className="integra-topbar-leading">
        <ButtonBase className="integra-brand-button" onClick={()=>navigate('/dashboard')} aria-label="Integra · Ir al inicio" disableRipple><img src="/Imagenes/integra_color.png" alt="Integra" draggable={false}/></ButtonBase>
        {onHome && <nav className="integra-module-nav" aria-label="Navegación del módulo">
          <ButtonBase className="integra-module-link" onClick={onHome} aria-label={subtitulo ? 'Volver al menú de '+subtitulo : 'Ver todos los módulos'} disableRipple>
            {subtitulo ? <ArrowBackRounded/> : <AppsRounded/>}<span>{subtitulo || 'Módulos'}</span>
          </ButtonBase>
        </nav>}
      </div>
      <ButtonBase id={menuId+'-trigger'} className={'integra-account-button'+(accountOpen?' is-open':'')} aria-label={'Menú de usuario: '+accountName} aria-controls={accountOpen?menuId:undefined} aria-haspopup="menu" aria-expanded={accountOpen} onClick={event=>setAccountAnchor(event.currentTarget)} disableRipple>
        <Avatar className="integra-account-avatar">{initials}</Avatar>
        <span className="integra-account-copy"><span>Mi cuenta</span><strong>{accountName}</strong></span>
        <KeyboardArrowDownRounded className="integra-account-chevron"/>
      </ButtonBase>
    </div>
    <Menu id={menuId} anchorEl={accountAnchor} open={accountOpen} onClose={closeAccount} className="integra-account-menu" disableScrollLock
      anchorOrigin={{vertical:'bottom',horizontal:'right'}} transformOrigin={{vertical:'top',horizontal:'right'}} transitionDuration={0}
      PaperProps={{className:'integra-account-paper'}} MenuListProps={{'aria-labelledby':menuId+'-trigger'}}>
      <AccountMenuSummary><Avatar className="integra-account-avatar">{initials}</Avatar><div><span>Sesión iniciada como</span><strong>{accountName}</strong></div></AccountMenuSummary>
      <Divider component="li"/>
      <MenuItem onClick={()=>{closeAccount();navigate('/perfil');}} disableRipple><AccountCircleOutlined/><span>Mi perfil</span></MenuItem>
      <MenuItem onClick={()=>{closeAccount();navigate('/dashboard');}} disableRipple><AppsRounded/><span>Menú principal</span></MenuItem>
      {subtitulo && onHome && <MenuItem onClick={()=>{closeAccount();onHome();}} disableRipple><ArrowBackRounded/><span>Ir a {subtitulo}</span></MenuItem>}
      <Divider component="li"/>
      <MenuItem className="integra-account-logout" onClick={logout} disableRipple><Logout/><span>Cerrar sesión</span></MenuItem>
    </Menu>
  </Box>
  {uso && (
    <UsoPopup open={popupAbierto} onClose={()=>setPopupAbierto(false)} estado={uso.estado} fechaLimite={uso.fechaLimite} diasRestantes={uso.diasRestantes} diasParaBloqueoTotal={uso.diasParaBloqueoTotal} />
  )}
  </>
  );
};

// ── Sección ────────────────────────────────────────────────────────────────────
export const SeccionApps = ({ label, color = IOS.label2, children }) => (
  <Box component="section" aria-label={label} className="integra-app-section" sx={{ mb:3 }}>
    <Box sx={{display:'flex', alignItems:'center', gap:1, mb:2.5}}>
      <Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:color}} />
      <Typography component="h2" sx={{fontSize:'0.9rem',fontWeight:650,color:IOS.label,letterSpacing:'-0.015em'}}>{label}</Typography>
    </Box>
    {children}
  </Box>
);

export const MenuPageIntro = ({ title, description }) => (
  <Box className="integra-page-intro" sx={{mb:3.5}}>
    <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.purple,letterSpacing:'.12em',textTransform:'uppercase',mb:1}}>Tu espacio de trabajo</Typography>
    <Typography component="h1" sx={{fontSize:{xs:'1.9rem',sm:'2.4rem'},fontWeight:700,letterSpacing:'-.045em',color:IOS.label,lineHeight:1.15}}>{title}</Typography>
    <Typography sx={{mt:1,fontSize:'.9rem',color:IOS.label2,lineHeight:1.6}}>{description}</Typography>
  </Box>
);

// ── Tarjeta app ────────────────────────────────────────────────────────────────
export const TarjetaApp = ({ id, nombre, iconKey, ruta, bloqueado, onHover, onLeave, onClick, badge }) => {
  const navigate = useNavigate();
  const color = COLOR[iconKey || id] || IOS.blue;
  const Icono = ICONOS_MODULOS[iconKey || id];
  return (
    <ButtonBase className="integra-app" disableRipple disabled={Boolean(bloqueado)}
      aria-label={nombre + (bloqueado ? ', no disponible' : '') + (badge > 0 ? ', ' + badge + ' pendientes' : '')}
      onMouseEnter={() => onHover?.(id)} onMouseLeave={onLeave}
      onClick={() => { if (onClick) onClick(); if (ruta) navigate(ruta); }}
      sx={{'--app-color':color, width:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-start',
        overflow:'visible', borderRadius:'20px', p:0.7, minHeight:128, color:IOS.label,
        '&.Mui-disabled':{color:'#9197A1'}}}>
      <Box className="integra-app-icon" sx={{position:'relative',width:'100%',maxWidth:86,aspectRatio:'1',
        borderRadius:'22px',display:'flex',alignItems:'center',justifyContent:'center',mb:1.3,
        '--icon-surface':bloqueado ? '#F0F1F3' : '#FFFFFF',
        background:bloqueado ? '#F0F1F3' : '#FFFFFF', color:bloqueado ? '#A5AAB3' : '#777D87',
        border:'1px solid '+(bloqueado ? '#E8EAEE' : '#E4E7EC'),boxShadow:'none'}}>
        {Icono ? <Icono color="currentColor" size={36}/> : <Home sx={{fontSize:32,color:'inherit'}}/>}
        {!bloqueado && badge > 0 && <Box sx={{position:'absolute',top:-5,right:-5,minWidth:22,height:22,px:.6,borderRadius:12,bgcolor:IOS.red,color:'white',fontSize:'.68rem',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 0 0 3px #fff'}}>{badge > 99 ? '99+' : badge}</Box>}
        {bloqueado && <Box sx={{position:'absolute',right:4,bottom:4,width:22,height:22,bgcolor:'#fff',borderRadius:'50%',display:'grid',placeItems:'center'}}><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#687184" strokeWidth="2"><rect x="5" y="10" width="14" height="11" rx="3"/><path d="M8 10V7a4 4 0 018 0v3"/></svg></Box>}
      </Box>
      <Typography component="span" sx={{fontSize:{xs:'.7rem',sm:'.76rem'},fontWeight:550,lineHeight:1.4,textAlign:'center',overflowWrap:'anywhere'}}>{nombre}</Typography>
    </ButtonBase>
  );
};

export const InfoCard = ({ children, color }) => (
  <Box className="integra-info-card" sx={{bgcolor:color||IOS.surface,
    backgroundImage:color ? 'linear-gradient(135deg,rgba(255,255,255,.12),rgba(0,0,0,.08))' : 'none',
    borderRadius:'24px',p:{xs:2.5,sm:3},border:'1px solid '+(color ? 'rgba(255,255,255,.18)' : 'rgba(218,224,237,.7)'),
    boxShadow:color ? '0 10px 28px '+color+'22' : '0 6px 24px rgba(32,45,85,.045)',mb:2.5,minWidth:0}}>{children}</Box>
);
